import React, { useState, useEffect, memo } from 'react';
import { API_BASE_URL } from '../../../utils/apiConfig';

/**
 * RecentActivity - Widget "Activité récente" pour Admin
 */
const RecentActivity = memo(({ token, onNavigate }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchActivity = async () => {
      if (!token) return;

      setLoading(true);
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/analytics/dashboard/audit?limit=10`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (!response.ok) throw new Error('Erreur de chargement');

        const data = await response.json();
        const items = data.success ? (data.data?.items || data.data || []) : [];
        setActivities(Array.isArray(items) ? items : []);
      } catch (err) {
        console.error('Erreur fetch activity:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [token]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const getActionIcon = (action) => {
    const icons = {
      'create': '➕',
      'update': '✏️',
      'delete': '🗑️',
      'login': '🔐',
      'view': '👁️'
    };
    return icons[action] || '📋';
  };

  const getActionLabel = (activity) => {
    const action = activity.action || activity.event_name || '';
    const entity = activity.entity_type || activity.module || '';

    if (action.includes('create')) return `a créé ${entity}`;
    if (action.includes('update') || action.includes('edit')) return `a modifié ${entity}`;
    if (action.includes('delete')) return `a supprimé ${entity}`;
    if (action.includes('login')) return 'connexion';
    if (action.includes('view')) return `a consulté ${entity}`;

    return action;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Activité récente</h4>
        <div style={styles.loading}>Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <h4 style={styles.title}>Activité récente</h4>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>Activité récente</h4>
        <button
          style={styles.viewButton}
          onClick={() => onNavigate && onNavigate('analytics')}
        >
          Voir audit
        </button>
      </div>

      {!Array.isArray(activities) || activities.length === 0 ? (
        <div style={styles.empty}>
          <span style={styles.emptyIcon}>📋</span>
          <p style={styles.emptyText}>Aucune activité récente</p>
        </div>
      ) : (
        <div style={styles.list}>
          {(activities || []).slice(0, 6).map((activity, index) => (
            <div key={activity.id || index} style={styles.activityItem}>
              <span style={styles.time}>
                {formatTime(activity.created_at || activity.timestamp)}
              </span>
              <span style={styles.icon}>
                {getActionIcon(activity.action || activity.event_name)}
              </span>
              <span style={styles.description}>
                <strong>{activity.user_name || activity.userName || 'Utilisateur'}</strong>
                {' '}
                {getActionLabel(activity)}
                {activity.entity_id && (
                  <span style={styles.entityId}> #{activity.entity_id.substring(0, 8)}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

RecentActivity.displayName = 'RecentActivity';

const styles = {
  container: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    height: '100%',           // Permet de s'étirer à la hauteur du parent
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  title: {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a2e'
  },
  viewButton: {
    background: 'none',
    border: 'none',
    color: '#6366f1',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500'
  },
  loading: {
    textAlign: 'center',
    padding: '30px',
    color: '#6c757d'
  },
  error: {
    padding: '20px',
    backgroundColor: '#fff5f5',
    borderRadius: '8px',
    color: '#dc3545',
    textAlign: 'center'
  },
  empty: {
    textAlign: 'center',
    padding: '30px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  emptyIcon: {
    fontSize: '2rem',
    display: 'block',
    marginBottom: '8px'
  },
  emptyText: {
    margin: 0,
    color: '#6c757d'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1
  },
  activityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    fontSize: '0.9rem'
  },
  time: {
    fontSize: '0.8rem',
    color: '#6c757d',
    minWidth: '50px'
  },
  icon: {
    fontSize: '1rem'
  },
  description: {
    flex: 1,
    color: '#495057',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  entityId: {
    color: '#6c757d',
    fontSize: '0.8rem'
  }
};

export default RecentActivity;
